// src/components/CoverSliderArtistes.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import MediaRenderer from "./MediaRenderer";
import MusicEmbedModal from "./MusicEmbedModal";
import { getSpotifyEmbed, normalizeLink } from "../lib/musicEmbeds";

export default function CoverSliderArtistes() {
  const navigate = useNavigate();
  const [covers, setCovers] = useState([]);
  const [coverLinks, setCoverLinks] = useState([]);
  const [title, setTitle] = useState("Découvrez nos pochettes");
  const [current, setCurrent] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [musicPlayer, setMusicPlayer] = useState(null);
  const hoverRef = useRef(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    const update = () => setIsMobile(window.innerWidth < 640);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  // Firestore temps réel
  useEffect(() => {
    const ref = doc(db, "admin", "artistes");
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) return;
      const d = snap.data();
      setCovers(d.covers || []);
      setCoverLinks(d.coversLinks || []);
      setTitle(d.coversTitle || "Découvrez nos pochettes");
      setCurrent(0);
    });
    return () => unsub();
  }, []);

  // autoplay
  useEffect(() => {
    if (!covers.length) return;
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      if (!hoverRef.current) {
        setCurrent((i) => (i + 1) % covers.length);
      }
    }, 2000);
    return () => clearInterval(intervalRef.current);
  }, [covers.length]);

  // fenêtre visible
  const visible = useMemo(() => {
    const n = covers.length;
    if (!n) return [];
    const offsets = isMobile
      ? n === 1 ? [0] : [-1, 0, 1]
      : n === 1 ? [0] : n === 2 ? [-1, 0] : n === 3 ? [-1, 0, 1] : [-2, -1, 0, 1, 2];
    return offsets.map((k) => {
      const idx = ((current + k) % n + n) % n;
      return {
        k,
        idx,
        url: covers[idx],
        link: normalizeLink(coverLinks[idx]),
      };
    });
  }, [covers, coverLinks, current, isMobile]);

  if (!covers.length) return null;

  const sizeForK = (k) => {
    const d = Math.abs(k);
    if (isMobile) {
      if (d === 0) return 250;
      if (d === 1) return 135;
      return 100;
    }
    if (d === 0) return 320;
    if (d === 1) return 200;
    return 160;
  };

  const styleForK = (k) => {
    const w = sizeForK(k);
    const d = Math.abs(k);
    const opacity = d === 0 ? 1 : d === 1 ? 0.9 : 0.7;
    const z = d === 0 ? 40 : d === 1 ? 25 : 10;
    return {
      position: "absolute",
      left: "50%",
      top: "50%",
      width: `${w}px`,
      height: `${w}px`,
      transform: `translate(-50%, -50%) translateX(${k * (isMobile ? 120 : 220)}px)`,
      transition:
        "transform 500ms ease, opacity 500ms ease, width 500ms ease, height 500ms ease",
      opacity,
      zIndex: z,
      borderRadius: "20px",
      overflow: "hidden",
      cursor: "pointer",
      boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
    };
  };

  return (
    <section
      className="relative bg-black text-white py-16"
      onMouseEnter={() => (hoverRef.current = true)}
      onMouseLeave={() => (hoverRef.current = false)}
    >
      <h2 className="text-2xl md:text-3xl font-bold px-6 md:px-12 mb-8">
        {title}
      </h2>

      <div className="relative mx-auto" style={{ height: isMobile ? 300 : 360 }}>
        <div className="relative overflow-hidden" style={{ height: isMobile ? 300 : 360 }}>
          {visible.map(({ k, idx, url, link }) => {
            const isActive = idx === current;
            const spotify = getSpotifyEmbed(link);
            return (
            <button
              type="button"
              key={`${idx}-${url}`}
              style={styleForK(k)}
              onClick={() => {
                if (!isActive) {
                  setCurrent(idx);
                } else if (link) {
                  if (spotify) {
                    setMusicPlayer({
                      ...spotify,
                      title: `${spotify.label} Unlimited Session`,
                    });
                  } else if (link.startsWith("/")) {
                    navigate(link);
                  } else {
                    window.open(link, "_blank", "noopener,noreferrer");
                  }
                }
              }}
              aria-label={spotify ? `Écouter la pochette ${idx + 1}` : `Pochette ${idx + 1}`}
              className="group rounded-2xl"
            >
              <MediaRenderer
                src={url}
                type="auto"
                alt={`Cover ${idx + 1}`}
                background
                className="w-full h-full object-cover rounded-2xl"
                loading="lazy"
                draggable={false}
              />
              {isActive && link && (
                <span className="absolute bottom-3 left-1/2 z-10 -translate-x-1/2 rounded-full border border-white/20 bg-black/65 px-4 py-2 text-[11px] font-bold uppercase tracking-wide text-white opacity-0 backdrop-blur transition group-hover:opacity-100">
                  {spotify ? "Écouter ici" : "Ouvrir"}
                </span>
              )}
            </button>
            );
          })}
        </div>
      </div>
      <MusicEmbedModal
        player={musicPlayer}
        title={musicPlayer?.title || "Unlimited Session"}
        onClose={() => setMusicPlayer(null)}
      />
    </section>
  );
}
